import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Faq } from "../components/Faq";
import { CtaBand } from "../components/CtaBand";
import { Seo, faqJsonLd, breadcrumbJsonLd, serviceJsonLd } from "../components/Seo";
import { T, paths } from "../i18n";
import { getService } from "../data/services";
import { CITIES } from "../data/cities";
import { serviceContent } from "../content";
import { isIndexableCombo } from "../seoPolicy";

export default function ServicePage({ lang }) {
  const { serviceSlug } = useParams();
  const t = T[lang];
  const p = paths(lang);
  const s = getService(serviceSlug);
  const content = serviceContent(serviceSlug, lang);
  if (!s || !content) return <Navigate to={p.services} replace />;

  const Icon = s.icon;
  const visibleCities = CITIES.filter((city) => isIndexableCombo(serviceSlug, city.slug));

  return (
    <div data-testid={`service-page-${serviceSlug}`}>
      <Seo lang={lang} title={content.title} description={content.description}
        enPath={`/security-services/${serviceSlug}`} frPath={`/fr/services-de-securite/${serviceSlug}`}
        jsonLd={[
          serviceJsonLd(content.h1, content.description, (lang === "fr" ? `/fr/services-de-securite/${serviceSlug}` : `/security-services/${serviceSlug}`), "Quebec, Canada"),
          faqJsonLd(content.faq),
          breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.breadcrumbServices, p.services], [s[lang].name, p.service(serviceSlug)]])
        ]} />
      <PageHeader lang={lang} eyebrow={t.nav.services} title={content.h1}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.breadcrumbServices, p.services], [s[lang].name, null]]} />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Reveal>
              <div className="mb-6 inline-flex rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4"><Icon size={34} className="text-amber-400" /></div>
              {content.body.map((para, i) => (
                <p key={i} className="mb-5 text-base leading-relaxed text-slate-300">{para}</p>
              ))}
            </Reveal>

            <Reveal>
              <h2 className="mt-10 font-display text-2xl font-bold tracking-tight sm:text-3xl">{t.includedTitle}</h2>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {s[lang].features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 rounded-xl border border-slate-800/70 bg-card/60 px-4 py-3 text-sm text-slate-300" data-testid="service-feature">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-amber-400" /> {f}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal>
              <h2 className="mt-12 font-display text-2xl font-bold tracking-tight sm:text-3xl">{t.industriesTitle}</h2>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {t.industries.map((ind) => (
                  <span key={ind} className="rounded-full border border-slate-700 bg-card/60 px-4 py-2 text-sm text-slate-300">{ind}</span>
                ))}
              </div>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-2xl border border-amber-500/25 bg-card/80 p-6">
              <h2 className="font-display text-lg font-bold">{t.ctaBandTitle}</h2>
              <p className="mt-2 text-sm text-slate-400">{t.ctaBandSub}</p>
              <a href="tel:+18077700243" className="mt-5 block rounded-full bg-amber-500 px-5 py-3 text-center text-sm font-bold text-obsidian transition-colors hover:bg-amber-300" data-testid="service-aside-call">+1 807-770-0243</a>
              <a href="mailto:admin@1stnation-security.com" className="mt-3 block rounded-full border border-amber-500/50 px-5 py-3 text-center text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10" data-testid="service-aside-email">admin@1stnation-security.com</a>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t border-slate-800/60 bg-card/30 py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t.whereTitle}</h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-400">
              {lang === "fr"
                ? `Nos équipes de ${s.fr.name.toLowerCase()} desservent les villes du Québec. Choisissez votre ville :`
                : `Our ${s.en.name.toLowerCase()} teams serve Quebec cities. Choose your city:`}
            </p>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {visibleCities.map((city) => (
              <Link key={city.slug} to={p.combo(serviceSlug, city.slug)} className="rounded-full border border-slate-800 bg-obsidian/60 px-4 py-2 text-sm text-slate-300 transition-all hover:border-amber-500/50 hover:text-amber-300" data-testid={`service-city-link-${city.slug}`}>
                {lang === "fr" ? city.fr : city.en}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Faq items={content.faq} title={t.faqTitle} />
      <CtaBand lang={lang} />
    </div>
  );
}
